import { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { compareAnalyzeSchedule, compareSyncs } from "../lib/api";
import { toast } from "sonner";
import { ArrowRightLeft, Sparkles } from "lucide-react";

export default function SyncComparator({ syncs }) {
  const { t } = useI18n();
  const [base, setBase] = useState("");
  const [target, setTarget] = useState("");
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (!syncs?.length) return;
    const baseline = syncs.find((s) => s.is_baseline) || syncs[syncs.length - 1];
    const latest = syncs[0];
    if (baseline && latest && baseline.id !== latest.id) {
      setBase(baseline.id);
      setTarget(latest.id);
    } else if (syncs[0]) {
      setBase(syncs[0]?.id || "");
      setTarget(syncs[1]?.id || syncs[0]?.id || "");
    }
  }, [syncs]);

  const onCompare = async () => {
    if (!base || !target || base === target) {
      toast.error("Selecione duas versões diferentes");
      return;
    }
    setLoading(true); setAiResult(null);
    try {
      const d = await compareSyncs(base, target);
      setDiff(d);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setLoading(false); }
  };

  const onAiCompare = async () => {
    if (!base || !target || base === target) return;
    setAiLoading(true);
    try {
      const r = await compareAnalyzeSchedule({ base_sync_id: base, target_sync_id: target });
      setAiResult(r.result);
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setAiLoading(false); }
  };

  return (
    <div data-testid="sync-comparator">
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="cs-label block mb-1.5">{t("select_baseline")}</label>
          <select value={base} onChange={(e) => setBase(e.target.value)} className="px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono min-w-[200px]" data-testid="compare-base-select">
            <option value="">—</option>
            {syncs?.map((s) => <option key={s.id} value={s.id}>v{s.version} · {s.name}</option>)}
          </select>
        </div>
        <ArrowRightLeft size={16} className="text-zinc-400 mb-3" />
        <div>
          <label className="cs-label block mb-1.5">{t("select_target")}</label>
          <select value={target} onChange={(e) => setTarget(e.target.value)} className="px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono min-w-[200px]" data-testid="compare-target-select">
            <option value="">—</option>
            {syncs?.map((s) => <option key={s.id} value={s.id}>v{s.version} · {s.name}</option>)}
          </select>
        </div>
        <button onClick={onCompare} disabled={loading || !base || !target} className="px-4 py-2 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-60" data-testid="compare-btn">
          {loading ? "..." : t("compare")}
        </button>
        <button onClick={onAiCompare} disabled={aiLoading || !base || !target} className="px-4 py-2 bg-cs-accent text-white text-sm font-semibold rounded-md hover:bg-cs-accentHover disabled:opacity-60 inline-flex items-center gap-1.5" data-testid="compare-ai-btn">
          <Sparkles size={14} /> {aiLoading ? t("generating") : t("compare_with_ai")}
        </button>
      </div>

      {aiResult && (
        <div className="mb-6 bg-zinc-950 text-white rounded-md p-5 border border-zinc-800" data-testid="compare-ai-result">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-cs-accent" />
            <span className="cs-label !text-white/60">claude sonnet 4.5</span>
          </div>
          <p className="text-sm text-balance leading-relaxed">{aiResult.summary}</p>
          {aiResult.recommendations?.length > 0 && (
            <ul className="mt-4 space-y-1.5 text-sm">
              {aiResult.recommendations.slice(0, 5).map((r, i) => (
                <li key={`rec-${i}-${r.slice(0, 20)}`} className="flex gap-2"><span className="text-cs-accent">›</span><span className="text-white/90">{r}</span></li>
              ))}
            </ul>
          )}
        </div>
      )}

      {diff && (
        <div data-testid="compare-diff">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Metric label={t("added")} value={diff.summary.added_count} color="text-cs-success" />
            <Metric label={t("removed")} value={diff.summary.removed_count} color="text-cs-critical" />
            <Metric label={t("delayed")} value={diff.summary.delayed_count} color="text-red-700" />
            <Metric label={t("accelerated")} value={diff.summary.accelerated_count} color="text-cs-success" />
            <Metric label={t("unchanged")} value={diff.summary.unchanged_count} color="text-zinc-700" />
          </div>

          {diff.delayed?.length > 0 && <DiffSection title={t("delayed")} tone="red" items={diff.delayed} renderItem={(it) => <DiffRow it={it} tone="red" />} />}
          {diff.accelerated?.length > 0 && <DiffSection title={t("accelerated")} tone="green" items={diff.accelerated} renderItem={(it) => <DiffRow it={it} tone="green" />} />}
          {diff.added?.length > 0 && <DiffSection title={t("added")} tone="green" items={diff.added} renderItem={(it) => <SimpleRow it={it} />} />}
          {diff.removed?.length > 0 && <DiffSection title={t("removed")} tone="red" items={diff.removed} renderItem={(it) => <SimpleRow it={it} />} />}
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-3">
      <div className="cs-label">{label}</div>
      <div className={`font-mono text-2xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function DiffSection({ title, tone, items, renderItem }) {
  const headerTone = tone === "red" ? "bg-red-50 text-red-900 border-red-200" : "bg-green-50 text-green-900 border-green-200";
  return (
    <div className="mb-5">
      <div className={`border ${headerTone} px-4 py-2 rounded-t-md font-mono text-xs font-semibold uppercase tracking-wider`}>{title} · {items.length}</div>
      <div className="bg-white border border-t-0 border-zinc-200 rounded-b-md">
        <div className="divide-y divide-zinc-100 max-h-72 overflow-y-auto scrollbar-thin">
          {items.slice(0, 100).map((it, i) => {
            // Diff items come from server-side compare — they always carry a
            // task_id on either base or target. Fall back to index only when
            // neither is present (shouldn't happen in practice).
            const stableKey = it.target?.task_id || it.base?.task_id || it.task_id || `it-${i}`;
            return <div key={stableKey} className="px-4 py-2.5">{renderItem(it)}</div>;
          })}
        </div>
      </div>
    </div>
  );
}

function DiffRow({ it, tone }) {
  const bgChange = tone === "red" ? "bg-red-50" : "bg-green-50";
  return (
    <div className="flex items-start justify-between gap-3 text-xs">
      <div className="min-w-0 flex-1">
        <div className="font-mono text-[10px] text-zinc-500">{it.target?.task_id}</div>
        <div className="font-medium text-zinc-900 truncate" title={it.target?.name}>{it.target?.name}</div>
      </div>
      <div className={`font-mono text-[11px] px-2 py-1 rounded ${bgChange}`}>
        {(it.base?.finish || "").slice(0, 10) || "—"} → {(it.target?.finish || "").slice(0, 10) || "—"}
      </div>
    </div>
  );
}

function SimpleRow({ it }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <div className="min-w-0 flex-1 truncate text-zinc-900">{it.name}</div>
      <span className="font-mono text-[10px] text-zinc-500 ml-2">{it.task_id}</span>
    </div>
  );
}
